import type { Composition } from "@reagent/sim";
import { PALETTE } from "./render/palette";

/**
 * What each reagent looks like — and, crucially, what shape it is.
 *
 * `04-art-direction.md` asks for "bright pastel fluid colors ... for high reagent
 * contrast", and until now that was the whole story: every packet on the board
 * was drawn the same shade of sky blue regardless of what was in it, so the
 * board couldn't tell you whether the thing crossing that pipe was acid, base
 * or product. Colour alone would not have been enough anyway. Around one man in
 * twelve cannot separate the terracotta-vs-sage end of this palette, and this
 * game asks you to read a moving stream at a glance and decide whether to spend
 * a card on it — so **every reagent carries a silhouette as well as a colour**,
 * and the silhouette is the primary channel.
 *
 * Pairs are authored rather than generated so that species which share a board
 * never share a look: stage 04 has O2 and H2O in the air at once, stage 03 has
 * CaCO3 and CaO, and those are exactly the pairs a player must not confuse.
 * Anything unauthored still gets a stable, distinct look from its name, so
 * adding a reagent to @reagent/content is never invisible on the board.
 */

export type ReagentShape = "sphere" | "cube" | "diamond" | "rod" | "ring" | "cross" | "wedge";

export interface ReagentLook {
  readonly species: string;
  /** Typeset formula for the HUD; the raw species id stays the key. */
  readonly label: string;
  /**
   * A plain-language name to sit next to the formula in the inspector — a
   * player reading "H₂SO₄" and "Sulfuric acid" together learns the formula;
   * the formula alone is just a glyph to memorise.
   *
   * Drafted for spec 001 open question 1 and not yet chemistry-reviewed. Flags
   * from that spec: "Glycol" below is ethylene glycol (not ethylene oxide),
   * C2H4O is ethylene oxide, and CaO is quicklime (calcium oxide) rather than
   * the everyday "lime" (calcium hydroxide, a different compound).
   */
  readonly commonName: string;
  readonly color: string;
  readonly shape: ReagentShape;
}

const AUTHORED: Record<string, Omit<ReagentLook, "species">> = {
  HCl: { label: "HCl", commonName: "Hydrochloric acid", color: PALETTE.fluidMagenta, shape: "diamond" },
  NaOH: { label: "NaOH", commonName: "Sodium hydroxide", color: PALETTE.fluidAmber, shape: "cube" },
  NaCl: { label: "NaCl", commonName: "Table salt", color: PALETTE.fluidMintGreen, shape: "cross" },
  H2O: { label: "H₂O", commonName: "Water", color: PALETTE.fluidSkyBlue, shape: "sphere" },
  H2SO4: { label: "H₂SO₄", commonName: "Sulfuric acid", color: PALETTE.mustard, shape: "diamond" },
  NH3: { label: "NH₃", commonName: "Ammonia", color: PALETTE.fluidMintGreen, shape: "ring" },
  AmSO4: {
    label: "(NH₄)₂SO₄",
    commonName: "Ammonium sulfate",
    color: PALETTE.terracotta,
    shape: "cross",
  },
  CaCO3: { label: "CaCO₃", commonName: "Limestone", color: PALETTE.chassisGrey, shape: "cube" },
  CaO: { label: "CaO", commonName: "Quicklime", color: PALETTE.cream, shape: "wedge" },
  CO2: { label: "CO₂", commonName: "Carbon dioxide", color: PALETTE.sage, shape: "sphere" },
  C2H4: { label: "C₂H₄", commonName: "Ethylene", color: PALETTE.fluidMintGreen, shape: "rod" },
  O2: { label: "O₂", commonName: "Oxygen", color: PALETTE.cream, shape: "cube" },
  C2H4O: { label: "C₂H₄O", commonName: "Ethylene oxide", color: PALETTE.fluidMagenta, shape: "wedge" },
  Glycol: { label: "Glycol", commonName: "Ethylene glycol", color: PALETTE.fluidAmber, shape: "ring" },
};

/** Every species with an authored look, for tests that must not miss one. */
export const AUTHORED_SPECIES: readonly string[] = Object.keys(AUTHORED);

const FALLBACK_SHAPES: readonly ReagentShape[] = [
  "sphere",
  "cube",
  "diamond",
  "rod",
  "ring",
  "cross",
  "wedge",
];

const FALLBACK_COLORS: readonly string[] = [
  PALETTE.fluidSkyBlue,
  PALETTE.fluidMagenta,
  PALETTE.fluidMintGreen,
  PALETTE.fluidAmber,
  PALETTE.mustard,
  PALETTE.sage,
];

/** FNV-1a. Any stable string hash would do; this one is short and has no deps. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const cache = new Map<string, ReagentLook>();

/**
 * The look of one species. Unauthored names are hashed rather than defaulted,
 * so a new reagent gets its own silhouette instead of hiding behind whatever
 * the last one used.
 */
export function reagentLook(species: string): ReagentLook {
  const cached = cache.get(species);
  if (cached) return cached;

  const authored = AUTHORED[species];
  const look: ReagentLook = authored
    ? { species, ...authored }
    : {
        species,
        label: species,
        commonName: species,
        color: FALLBACK_COLORS[hash(species) % FALLBACK_COLORS.length]!,
        // A second, differently-mixed hash, or shape and colour would move
        // together and half the possible looks would never be used.
        shape: FALLBACK_SHAPES[hash(`${species}#shape`) % FALLBACK_SHAPES.length]!,
      };

  cache.set(species, look);
  return look;
}

/**
 * The species a packet reads as. A packet is a mixture, and the board has one
 * silhouette to spend on it, so the largest constituent wins — ties broken by
 * name, because a packet must not flicker between two looks as it travels.
 */
export function dominantSpecies(composition: Composition): string | null {
  let best: string | null = null;
  let bestUnits = 0;
  for (const [species, units] of Object.entries(composition)) {
    if (units > bestUnits || (units === bestUnits && best !== null && species < best)) {
      best = species;
      bestUnits = units;
    }
  }
  return best;
}

export function lookOf(composition: Composition): ReagentLook | null {
  const species = dominantSpecies(composition);
  return species === null ? null : reagentLook(species);
}

/** Every species in a composition, largest first — for the "what's in it" readouts. */
export function breakdown(composition: Composition): ReadonlyArray<{ look: ReagentLook; units: number }> {
  return Object.entries(composition)
    .filter(([, units]) => units > 0)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .map(([species, units]) => ({ look: reagentLook(species), units }));
}
